import { Component, OnInit, Inject, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { CardPaymentMethod, APIDataResponse } from 'src/app/interfaces';
import { HttpService } from 'src/app/services/http.service';
import { HttpErrorResponse } from '@angular/common/http';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { PaymentMethodsService } from 'src/app/services/payment-methods.service';

export interface CardInscriptionDialogData {
  storeId: string | null;
  origin: string;
  acceptedPaymentMethods?: {[key: string]: boolean};
}

@Component({
  selector: 'app-card-inscription-selection',
  templateUrl: './card-inscription-selection.component.html',
  styleUrls: ['./card-inscription-selection.component.css']
})
export class CardInscriptionSelectionComponent implements OnInit {

  storeId: string | null = null;
  mercadopagoAccepted: boolean;
  transbankAccepted: boolean;
  klapAccepted: boolean;
  totalAccepted: number = 0;
  origin: string = 'unknown';

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public data: CardInscriptionDialogData | null,
    private spinner: NgxSpinnerService,
    private http: HttpService,
    private router: Router,
    private analytics: AnalyticsService,
    public dialog: MatDialog,
    private paymentMethodsService: PaymentMethodsService
  ) {
    this.storeId = data?.storeId ?? null;
    this.origin = data?.origin ?? 'unknown';

    // Get enabled card providers from service cache
    const enabledProviders = this.paymentMethodsService.getEnabledCardProviders();

    this.transbankAccepted = enabledProviders.has(CardPaymentMethod.TRANSBANK);
    if (data?.acceptedPaymentMethods) {
      const transbankAcceptedByStore =  data?.acceptedPaymentMethods[CardPaymentMethod.TRANSBANK] || false;
      this.transbankAccepted = transbankAcceptedByStore && this.transbankAccepted;
    }

    this.mercadopagoAccepted = enabledProviders.has(CardPaymentMethod.MERCADOPAGO);
    if (data?.acceptedPaymentMethods) {
      const mercadopagoAcceptedByStore =  data?.acceptedPaymentMethods[CardPaymentMethod.MERCADOPAGO] || false;
      this.mercadopagoAccepted = mercadopagoAcceptedByStore && this.mercadopagoAccepted;
    }

    this.klapAccepted = enabledProviders.has(CardPaymentMethod.KLAP);
    if (data?.acceptedPaymentMethods) {
      const klapAcceptedByStore =  data?.acceptedPaymentMethods[CardPaymentMethod.KLAP] || false;
      this.klapAccepted = klapAcceptedByStore && this.klapAccepted;
    }

    if (this.transbankAccepted) {
      this.totalAccepted += 1;
    }
    if (this.mercadopagoAccepted) {
      this.totalAccepted += 1;
    }
    if (this.klapAccepted) {
      this.totalAccepted += 1;
    }
  }

  ngOnInit(): void {
    this.analytics.logEvent("open_card_inscription", {"origin": this.origin});

    // Close the dialog if card inscription is disabled
    if (!this.transbankAccepted && !this.mercadopagoAccepted && !this.klapAccepted) {
      this.dialog.closeAll();
      return;
    }

    // Auto-select if only one payment method is available
    const availablePaymentMethods = this.getAvailablePaymentMethodsCount();
    if (availablePaymentMethods === 1) {
      this.autoSelectSinglePaymentMethod();
    }
  }

  /**
   * Counts the number of available payment methods
   * @returns number of available payment methods
   */
  private getAvailablePaymentMethodsCount(): number {
    let count = 0;
    if (this.transbankAccepted) count++;
    if (this.mercadopagoAccepted) count++;
    if (this.klapAccepted) count++;
    return count;
  }

  /**
   * Auto-selects and proceeds with the single available payment method
   * Reduces user friction by skipping the selection step when only one option exists
   */
  private autoSelectSinglePaymentMethod(): void {
    // Close the selection dialog since we're auto-selecting
    this.dialog.closeAll();

    // Proceed with the available payment method
    if (this.transbankAccepted) {
      this.transbankInscription();
    } else if (this.mercadopagoAccepted) {
      this.mercadopagoInscription();
    } else if (this.klapAccepted) {
      this.klapInscription();
    }
  }

  mercadopagoInscription() {
    this.analytics.logEvent("start_card_inscription", {"payment_processor": CardPaymentMethod.MERCADOPAGO, "origin": this.origin});
    this.router.navigate(
      ['mp-card-inscription'],
      { queryParams: {storeId: this.storeId },
        replaceUrl: false
      }
    );
  }

  transbankInscription() {
    this.analytics.logEvent("start_card_inscription", {"payment_processor": CardPaymentMethod.TRANSBANK, "origin": this.origin});
    this.spinner.show();
    this.http.transbankStartCardInscription(this.storeId)
    .then((inscriptionResponse: APIDataResponse<string>) => {
      this.spinner.hide();
      window.open(inscriptionResponse.data, "_self");
    })
    .catch((error: HttpErrorResponse) => {
      this.spinner.hide();
      console.log(error.error);
    })
  }

  klapInscription() {
    this.analytics.logEvent("start_card_inscription", {"payment_processor": CardPaymentMethod.KLAP, "origin": this.origin});
    this.router.navigate(
      ['klap-card-inscription'],
      { queryParams: {storeId: this.storeId },
        replaceUrl: false
      }
    );
  }

}
